import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../supabaseClient'


function EditLure() {
  const { lureId } = useParams()
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    brand_id: '',
    type_id: '',
    subtype_id: '',
    name: '',
    weight: '',
    length: '',
    depth_range: '',
    description: ''
  })

  const [brands, setBrands] = useState([])
  const [types, setTypes] = useState([])
  const [subtypes, setSubtypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [currentImageUrl, setCurrentImageUrl] = useState(null)

  useEffect(() => {
    fetchLureData()
    fetchDropdownData()
  }, [lureId])

  const fetchLureData = async () => {
    try {
      const { data, error } = await supabase
        .from('base_lures')
        .select('*')
        .eq('base_lure_id', lureId)
        .single()

      if (error) throw error

      setFormData({
        brand_id: data.brand_id.toString(),
        type_id: data.type_id.toString(),
        subtype_id: data.subtype_id ? data.subtype_id.toString() : '',
        name: data.name,
        weight: data.weight || '',
        length: data.length || '',
        depth_range: data.depth_range || '',
        description: data.description || ''
      })
      setCurrentImageUrl(data.image_url)  // ← ADD THIS LINE
      setLoading(false)
    } catch (err) {
      console.error('Error fetching lure:', err)
      setError('Failed to load lure')
      setLoading(false)
    }
  }

  const fetchDropdownData = async () => {
    try {
      const { data: brandsData } = await supabase
        .from('brands')
        .select('brand_id, brand_name')
        .order('brand_name')

      const { data: typesData } = await supabase
        .from('lure_types')
        .select('type_id, type_name')
        .order('type_name')

      const { data: subtypesData } = await supabase
        .from('lure_subtypes')
        .select('subtype_id, subtype_name')
        .order('subtype_name')

      setBrands(brandsData || [])
      setTypes(typesData || [])
      setSubtypes(subtypesData || [])
    } catch (err) {
      console.error('Error fetching dropdown data:', err)
    }
  }

const handleChange = (e) => {
  const { name, value } = e.target
  setFormData(prev => ({
    ...prev,
    [name]: value
  }))
  setError('')
}

// ============================================
// ADD ALL THESE NEW FUNCTIONS:
// ============================================

const handleImageSelect = (e) => {
  const file = e.target.files[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    setError('Please select an image file')
    return
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    setError('Image must be less than 5MB')
    return
  }

  setImageFile(file)
  
  // Create preview
  const reader = new FileReader()
  reader.onloadend = () => {
    setImagePreview(reader.result)
  }
  reader.readAsDataURL(file)
  setError('')
}

const uploadImage = async () => {
  if (!imageFile) return currentImageUrl

  setUploading(true)

  try {
    // Create unique filename
    const fileExt = imageFile.name.split('.').pop()
    const fileName = `base/${lureId}-${Date.now()}.${fileExt}`

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('lure-images')
      .upload(fileName, imageFile)

    if (uploadError) throw uploadError

    // Get public URL
    const { data: publicURL } = supabase.storage
      .from('lure-images')
      .getPublicUrl(fileName)

    setUploading(false)
    return publicURL.publicUrl

  } catch (err) {
    console.error('Error uploading image:', err)
    setUploading(false)
    throw err
  }
}

const handleRemoveImage = () => {
  setImageFile(null)
  setImagePreview(null)
}

const handleSubmit = async (e) => {
  e.preventDefault()
  setSubmitting(true)
  setError('')

  if (!formData.brand_id || !formData.type_id || !formData.name) {
    setError('Brand, Type, and Name are required')
    setSubmitting(false)
    return
  }

  try {
    // Upload image if new one selected
    let imageUrl = currentImageUrl
    if (imageFile) {
      imageUrl = await uploadImage()
    }

    const updateData = {
      brand_id: parseInt(formData.brand_id),
      type_id: parseInt(formData.type_id),
      subtype_id: formData.subtype_id ? parseInt(formData.subtype_id) : null,
      name: formData.name.trim(),
      weight: formData.weight.trim() || null,
      length: formData.length.trim() || null,
      depth_range: formData.depth_range.trim() || null,
      description: formData.description.trim() || null,
      image_url: imageUrl  // ← ADD THIS LINE
    }

    const { error: updateError } = await supabase
      .from('base_lures')
      .update(updateData)
      .eq('base_lure_id', lureId)

    if (updateError) throw updateError

    setSuccess(true)
    setTimeout(() => {
      navigate('/admin/manage-lures')
    }, 1500)

  } catch (err) {
    console.error('Error updating lure:', err)
    setError(err.message || 'Failed to update lure')
    setSubmitting(false)
  }
}

  if (loading) {
    return <div className="loading">Loading lure...</div>
  }

  if (success) {
    return (
      <div className="admin-success">
        <h1>✅ Lure Updated Successfully!</h1>
        <p>Redirecting...</p>
      </div>
    )
  }

  return (
    <div className="admin-form-page">
      <h1>Edit Lure</h1>
      <p className="page-subtitle">Update lure details</p>

      {error && <div className="error-message">❌ {error}</div>}

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label htmlFor="brand_id">Brand *</label>
          <select
            id="brand_id"
            name="brand_id"
            value={formData.brand_id}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Select a brand...</option>
            {brands.map(brand => (
              <option key={brand.brand_id} value={brand.brand_id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="type_id">Lure Type *</label>
          <select
            id="type_id"
            name="type_id"
            value={formData.type_id}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Select a type...</option>
            {types.map(type => (
              <option key={type.type_id} value={type.type_id}>
                {type.type_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="subtype_id">Subtype (Optional)</label>
          <select
            id="subtype_id"
            name="subtype_id"
            value={formData.subtype_id}
            onChange={handleChange}
            className="form-select"
          >
            <option value="">None</option>
            {subtypes.map(subtype => (
              <option key={subtype.subtype_id} value={subtype.subtype_id}>
                {subtype.subtype_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="name">Lure Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Vision 110"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="weight">
            Weight
            <span className="field-hint">
              (include units: e.g., 1/2 oz, 3/8 oz, 14g, 0.5 oz)
            </span>
          </label>
          <input
            type="text"
            id="weight"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            placeholder="1/2 oz"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="length">
            Length
            <span className="field-hint">
              (include units: e.g., 4.3 in, 110 mm, 3-1/2 in)
            </span>
          </label>
          <input
            type="text"
            id="length"
            name="length"
            value={formData.length}
            onChange={handleChange}
            placeholder="4.3 in"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="depth_range">Depth Range</label>
          <input
            type="text"
            id="depth_range"
            name="depth_range"
            value={formData.depth_range}
            onChange={handleChange}
            placeholder="e.g., 0-4 ft, Surface, 6-12 ft"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            placeholder="Describe the lure's features, action, best use cases..."
            className="form-textarea"
          ></textarea>
        </div>

        {/* ============================================
            ADD THIS ENTIRE IMAGE SECTION:
            ============================================ */}
        <div className="form-group">
          <label htmlFor="image-upload">Lure Image</label>
          
          {/* Current Image */}
          {currentImageUrl && !imagePreview && (
            <div className="current-image-container">
              <img src={currentImageUrl} alt="Current lure" className="current-image" />
              <p className="image-label">Current Image</p>
            </div>
          )}

          {/* New Image Preview */}
          {imagePreview && (
            <div className="image-preview-container">
              <img src={imagePreview} alt="Preview" className="image-preview" />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="btn-remove-image"
              >
                ✕ Remove
              </button>
            </div>
          )}

          {/* Upload Input */}
          <input
            type="file"
            id="image-upload"
            accept="image/*"
            onChange={handleImageSelect}
            className="file-input"
          />
          <p className="field-hint">
            JPG, PNG, or WEBP. Max 5MB. Recommended: 800x600px or larger.
          </p>
        </div>

        <div className="form-actions">
          <Link to="/admin/manage-lures" className="btn-secondary">
            Cancel
          </Link>
            <button
                type="submit"
                className="btn-primary"
                disabled={submitting || uploading}
                >
                {uploading ? 'Uploading Image...' : submitting ? 'Updating...' : 'Update Lure'}
            </button>
        </div>
      </form>
    </div>
  )
}

export default EditLure